import { useCallback, useEffect, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import type { SyncSummary } from "./model";

interface ReportsDashboardPanelProps {
  farmId: string;
  sync: SyncSummary;
}

interface ReportDashboard {
  from: string;
  to: string;
  operations: {
    total: number;
    byType: Record<string, number>;
  };
  breeding: {
    spawnCount: number;
    activePairs: number;
    eggResults: Record<string, number>;
    averageHatchPercent?: number;
    freeSwimCount: number;
    growoutCount: number;
  };
  feeding: {
    rounds: number;
    scheduledTanks: number;
    completedTanks: number;
    missedTanks: number;
    feedingEvents: number;
    lateEvents: number;
  };
  waterHealthTreatment: {
    waterOperations: number;
    siphonOperations: number;
    healthObservations: number;
    mortalityOperations: number;
    deadFish: number;
    treatments: number;
    groupBaths: number;
  };
  tasksAudit: {
    tasksCreated: number;
    tasksCompleted: number;
    pendingCurrent: number;
    overdueCurrent: number;
    domainEvents: number;
  };
  recentActivity: Array<{
    eventType: string;
    aggregateType: string;
    aggregateId: string;
    performedAt: string;
    sourceChannel: string;
    actorName: string;
  }>;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : Number(value ?? 0) || 0;
}

function numberMap(value: unknown): Record<string, number> {
  return Object.fromEntries(
    Object.entries(record(value)).map(([key, item]) => [key, numberValue(item)]),
  );
}

function activityRows(value: unknown): ReportDashboard["recentActivity"] {
  if (!Array.isArray(value)) return [];

  return value.map((item) => {
    const row = record(item);
    return {
      eventType: String(row.eventType ?? ""),
      aggregateType: String(row.aggregateType ?? ""),
      aggregateId: String(row.aggregateId ?? ""),
      performedAt: String(row.performedAt ?? ""),
      sourceChannel: String(row.sourceChannel ?? ""),
      actorName: String(row.actorName ?? "کاربر"),
    };
  });
}

function parseDashboard(value: unknown): ReportDashboard {
  const root = record(value);
  const operations = record(root.operations);
  const breeding = record(root.breeding);
  const feeding = record(root.feeding);
  const water = record(root.waterHealthTreatment);
  const tasks = record(root.tasksAudit);
  const averageHatchPercent =
    breeding.averageHatchPercent === null || breeding.averageHatchPercent === undefined
      ? undefined
      : numberValue(breeding.averageHatchPercent);

  return {
    from: String(root.from ?? ""),
    to: String(root.to ?? ""),
    operations: {
      total: numberValue(operations.total),
      byType: numberMap(operations.byType),
    },
    breeding: {
      spawnCount: numberValue(breeding.spawnCount),
      activePairs: numberValue(breeding.activePairs),
      eggResults: numberMap(breeding.eggResults),
      ...(averageHatchPercent === undefined ? {} : { averageHatchPercent }),
      freeSwimCount: numberValue(breeding.freeSwimCount),
      growoutCount: numberValue(breeding.growoutCount),
    },
    feeding: {
      rounds: numberValue(feeding.rounds),
      scheduledTanks: numberValue(feeding.scheduledTanks),
      completedTanks: numberValue(feeding.completedTanks),
      missedTanks: numberValue(feeding.missedTanks),
      feedingEvents: numberValue(feeding.feedingEvents),
      lateEvents: numberValue(feeding.lateEvents),
    },
    waterHealthTreatment: {
      waterOperations: numberValue(water.waterOperations),
      siphonOperations: numberValue(water.siphonOperations),
      healthObservations: numberValue(water.healthObservations),
      mortalityOperations: numberValue(water.mortalityOperations),
      deadFish: numberValue(water.deadFish),
      treatments: numberValue(water.treatments),
      groupBaths: numberValue(water.groupBaths),
    },
    tasksAudit: {
      tasksCreated: numberValue(tasks.tasksCreated),
      tasksCompleted: numberValue(tasks.tasksCompleted),
      pendingCurrent: numberValue(tasks.pendingCurrent),
      overdueCurrent: numberValue(tasks.overdueCurrent),
      domainEvents: numberValue(tasks.domainEvents),
    },
    recentActivity: activityRows(root.recentActivity),
  };
}

function operationLabel(value: string): string {
  const labels: Record<string, string> = {
    water_measurement: "اندازه‌گیری آب",
    water_change: "تعویض آب",
    siphon: "سیفون",
    health_observation: "مشاهده سلامت",
    mortality: "تلفات",
    treatment: "درمان",
  };
  return labels[value] ?? value;
}

function eggResultLabel(value: string): string {
  const labels: Record<string, string> = {
    hatched: "هچ شده",
    eaten: "خورده شده",
    whitened: "سفید کرده",
    unknown: "نامشخص",
  };
  return labels[value] ?? value;
}

function eventLabel(value: string): string {
  return value.replaceAll("_", " ");
}

function persianDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "زمان نامشخص";

  return date.toLocaleString("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function loadDashboard(farmId: string, days: number): Promise<ReportDashboard> {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);

  const result = await supabase.rpc("reports_dashboard_v1", {
    target_farm_id: farmId,
    target_from: from.toISOString(),
    target_to: to.toISOString(),
  });

  if (result.error) throw result.error;
  return parseDashboard(result.data);
}

function StatCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string | number;
  note?: string;
}) {
  return (
    <article className="manager-card">
      <p className="section-kicker">{label}</p>
      <h3>{value}</h3>
      {note ? <p>{note}</p> : null}
    </article>
  );
}

export function ReportsDashboardPanel({ farmId, sync }: ReportsDashboardPanelProps) {
  const [days, setDays] = useState(30);
  const [dashboard, setDashboard] = useState<ReportDashboard>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      setDashboard(await loadDashboard(farmId, days));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری گزارش انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [days, farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const feedingCompletion =
    dashboard && dashboard.feeding.scheduledTanks > 0
      ? Math.round((dashboard.feeding.completedTanks / dashboard.feeding.scheduledTanks) * 100)
      : undefined;

  return (
    <>
      <section className="section-card" aria-labelledby="reports-dashboard-title">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">M5</p>
            <h2 id="reports-dashboard-title">گزارش مدیریتی</h2>
          </div>
          <button
            type="button"
            className="text-button"
            disabled={loading}
            onClick={() => void refresh()}
          >
            تازه‌سازی
          </button>
        </div>

        <p className="section-note">
          گزارش فقط از داده canonical ثبت‌شده ساخته می‌شود. مقدار ثبت‌نشده به‌عنوان صفرِ ساختگی یا نتیجه
          تخمینی نمایش داده نمی‌شود.
        </p>

        <fieldset className="action-chip-list">
          <legend>بازه گزارش</legend>
          {[7, 30, 90].map((range) => (
            <button
              key={range}
              type="button"
              className={days === range ? "secondary-button" : "text-button"}
              onClick={() => setDays(range)}
            >
              {range} روز
            </button>
          ))}
        </fieldset>

        {loading ? <p className="section-note">در حال بارگذاری گزارش…</p> : null}
        {error ? <p className="settings-warning">{error}</p> : null}
      </section>

      {dashboard ? (
        <>
          <section className="section-card" aria-labelledby="report-ops-title">
            <div className="section-title-row">
              <div>
                <p className="section-kicker">عملیات</p>
                <h2 id="report-ops-title">عملیات سالن</h2>
              </div>
              <span className="count-pill">{dashboard.operations.total}</span>
            </div>
            <div className="quick-grid">
              {Object.entries(dashboard.operations.byType).length === 0 ? (
                <p className="empty-state">در این بازه عملیات ثبت‌شده‌ای وجود ندارد.</p>
              ) : (
                Object.entries(dashboard.operations.byType).map(([type, count]) => (
                  <StatCard key={type} label={operationLabel(type)} value={count} />
                ))
              )}
            </div>
          </section>

          <section className="section-card" aria-labelledby="report-breeding-title">
            <p className="section-kicker">تکثیر</p>
            <h2 id="report-breeding-title">عملکرد تکثیر</h2>
            <div className="quick-grid">
              <StatCard label="تخم‌ریزی" value={dashboard.breeding.spawnCount} />
              <StatCard label="جفت فعال فعلی" value={dashboard.breeding.activePairs} />
              <StatCard label="شنای آزاد" value={dashboard.breeding.freeSwimCount} />
              <StatCard label="ورود به رشد" value={dashboard.breeding.growoutCount} />
              <StatCard
                label="میانگین هچ"
                value={
                  dashboard.breeding.averageHatchPercent === undefined
                    ? "نامشخص"
                    : `${dashboard.breeding.averageHatchPercent}٪`
                }
              />
            </div>
            <div className="work-list">
              {Object.entries(dashboard.breeding.eggResults).map(([result, count]) => (
                <div className="work-item pending" key={result}>
                  <span className="work-main">
                    <strong>{eggResultLabel(result)}</strong>
                    <span>نتیجه ثبت‌شده تخم</span>
                  </span>
                  <span className="work-status">{count}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="section-card" aria-labelledby="report-feeding-title">
            <p className="section-kicker">تغذیه</p>
            <h2 id="report-feeding-title">گزارش تغذیه</h2>
            <div className="quick-grid">
              <StatCard label="دور برنامه‌ریزی‌شده" value={dashboard.feeding.rounds} />
              <StatCard label="تانک هدف" value={dashboard.feeding.scheduledTanks} />
              <StatCard label="تکمیل‌شده" value={dashboard.feeding.completedTanks} />
              <StatCard label="از دست‌رفته" value={dashboard.feeding.missedTanks} />
              <StatCard label="ثبت غذا" value={dashboard.feeding.feedingEvents} />
              <StatCard label="ثبت با تأخیر" value={dashboard.feeding.lateEvents} />
              <StatCard
                label="نرخ تکمیل"
                value={feedingCompletion === undefined ? "نامشخص" : `${feedingCompletion}٪`}
              />
            </div>
          </section>

          <section className="section-card" aria-labelledby="report-health-title">
            <p className="section-kicker">آب و سلامت</p>
            <h2 id="report-health-title">آب، سلامت و درمان</h2>
            <div className="quick-grid">
              <StatCard label="عملیات آب" value={dashboard.waterHealthTreatment.waterOperations} />
              <StatCard label="سیفون" value={dashboard.waterHealthTreatment.siphonOperations} />
              <StatCard
                label="مشاهده سلامت"
                value={dashboard.waterHealthTreatment.healthObservations}
              />
              <StatCard
                label="ثبت تلفات"
                value={dashboard.waterHealthTreatment.mortalityOperations}
              />
              <StatCard label="تعداد تلفات" value={dashboard.waterHealthTreatment.deadFish} />
              <StatCard label="درمان" value={dashboard.waterHealthTreatment.treatments} />
              <StatCard label="حمام گروهی" value={dashboard.waterHealthTreatment.groupBaths} />
            </div>
          </section>

          <section className="section-card" aria-labelledby="report-task-title">
            <p className="section-kicker">کار و ممیزی</p>
            <h2 id="report-task-title">کارها و رویدادها</h2>
            <div className="quick-grid">
              <StatCard label="کار ایجادشده" value={dashboard.tasksAudit.tasksCreated} />
              <StatCard label="کار تکمیل‌شده" value={dashboard.tasksAudit.tasksCompleted} />
              <StatCard label="باز فعلی" value={dashboard.tasksAudit.pendingCurrent} />
              <StatCard label="عقب‌افتاده فعلی" value={dashboard.tasksAudit.overdueCurrent} />
              <StatCard label="رویداد دامنه" value={dashboard.tasksAudit.domainEvents} />
            </div>

            <div className="work-list">
              {dashboard.recentActivity.length === 0 ? (
                <p className="empty-state">در این بازه رویداد ممیزی ثبت نشده است.</p>
              ) : (
                dashboard.recentActivity.map((activity) => (
                  <div
                    className="work-item pending"
                    key={`${activity.performedAt}-${activity.aggregateId}-${activity.eventType}`}
                  >
                    <span className="work-main">
                      <strong>{eventLabel(activity.eventType)}</strong>
                      <span>
                        {activity.actorName} · {activity.aggregateType} ·{" "}
                        {persianDateTime(activity.performedAt)}
                      </span>
                    </span>
                    <span className="work-status">{activity.sourceChannel}</span>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="section-card" aria-labelledby="report-sync-title">
            <p className="section-kicker">Sync</p>
            <h2 id="report-sync-title">سلامت همگام‌سازی این دستگاه</h2>
            <div className="quick-grid">
              <StatCard label="در صف" value={sync.pending} />
              <StatCard label="در حال ارسال" value={sync.syncing} />
              <StatCard label="ارسال مجدد" value={sync.retryable} />
              <StatCard label="تعارض" value={sync.conflicts} />
            </div>
            <p className="section-note">
              این چهار مقدار محلی و مخصوص همین دستگاه هستند؛ آمار سرور با داده محلی ادغام یا جعل
              نمی‌شود.
            </p>
          </section>
        </>
      ) : null}
    </>
  );
}
